import { Outlet, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useMyGroupedExpensablesDataTable,
} from 'generated/resources/datatable.graphql'
import { ExpensePageFinanceCustomCategoryFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/expenses/components/expenses/DataTable/_Amount'
import { Card } from 'modules/expenses/components/expenses/DataTable/_Card'
import { Category } from 'modules/expenses/components/expenses/DataTable/_Category'
import { Date as EffectiveOn } from 'modules/expenses/components/expenses/DataTable/_Date'
import { Detail } from 'modules/expenses/components/expenses/DataTable/_Detail'
import { DocumentType } from 'modules/expenses/components/expenses/DataTable/_DocumentType'
import { EmptyState } from 'modules/expenses/components/expenses/DataTable/_EmptyState'
import { allFilters } from 'modules/expenses/components/expenses/DataTable/_Filters'
import { PaymentMethod } from 'modules/expenses/components/expenses/DataTable/_PaymentMethod'
import { SpendingAlertPill } from 'modules/expenses/components/expenses/DataTable/_SpendingAlertPill'
import { Status } from 'modules/expenses/components/expenses/DataTable/_Status'
import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import { useActions } from 'modules/expenses/components/expenses/DataTable/useActions'
import { useQuickFiltersContext } from 'modules/expenses/components/expenses/PageContents/QuickFiltersContext'
import { MyExpensesInsights } from 'modules/expenses/components/my_expenses/Datatable/_MyExpensesInsights'
import { Currency } from 'modules/expenses/hooks/useExpensesListPage'
import { Group } from 'modules/expenses/hooks/useGroup'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { canAnyExpensableAction } from 'modules/expenses/lib/expensables/availableActions'
import { isDraftGroup } from 'modules/expenses/lib/groups/group'
import { useMyExpenses } from 'modules/expenses/pages/my_expenses/MyExpensesContext'

import { Actions } from './_Actions'
import { Header } from './_Header'

interface Props {
  group: Group
  categories: ExpensePageFinanceCustomCategoryFragment[]
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  currencies: Currency[]
  subcategories: ExpensePageFinanceCustomCategoryFragment[]
  back: { href: string }
  hasReportOnBehalfFeature: boolean
  canSeeTransactions: boolean
  canCreateExpensesForOthers: boolean
  canCreateExpenses: boolean
  canUsePerDiems: boolean
}

export const MyGroupedExpensesDataTable = ({
  group,
  categories,
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  currencies,
  subcategories,
  back,
  hasReportOnBehalfFeature,
  canSeeTransactions,
  canCreateExpensesForOthers,
  canCreateExpenses,
  canUsePerDiems,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const { canSyncWithPayroll, canSyncWithBanking, section, expensableGroupsEnabled } = context

  const { filters, clearFilters } = useQuickFiltersContext()
  const { status, pendingApproval } = filters ?? {}
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const { actions, Modals } = useActions({
    currentPayrollPeriodOpen,
    availablePayrollCategories,
  })
  const { data, featureAvailabilities } = useMyExpenses()
  const customizationFields = data.expenses?.customizationFields ?? []

  const source = useMyGroupedExpensablesDataTable(
    [
      {
        identifier: 'merchant_name',
        title: i18n.t('expenses_v2.list.header.merchant_name'),
        kind: ProjectionKind.Custom,
        template: '{% if expense %} {{expense.merchantName}} {% endif %}',
        render: ({ expense, mileage, perDiem }) => {
          if (mileage) {
            return i18n.t('expenses_v2.expense.mileage_expense')
          } else if (perDiem) {
            return i18n.t('expenses_v2.expense.per_diem_expense')
          }
          return expense?.userMerchant
        },
      },
      {
        identifier: 'status',
        title: i18n.t('expenses_v2.list.header.status'),
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        render: ({ status: expenseStatus, readyToReviewIssues }) => {
          return <Status status={expenseStatus} readyToReviewIssues={readyToReviewIssues} />
        },
        sortBy: 'status',
      },
      {
        identifier: 'spending_alerts',
        title: i18n.t('expenses_v2.list.header.spending_alerts'),
        kind: ProjectionKind.Custom,
        template:
          '{% assign alerts = spendingAlertsTarget | concat: spendingAlertsSource %}{% assign failing_alerts = alerts | where: "status", "failed" %}{% assign unique_alerts = failing_alerts | uniq: "type" %}{% for alert in unique_alerts %}{{alert.type | i18n: "expenses.alerts"}}{% unless forloop.last %}, {% endunless %}{% endfor %}',
        render: (expensable: Expensable) => {
          return (
            <SpendingAlertPill
              expensableAmount={expensable.amount}
              alerts={[...expensable.spendingAlertsTarget, ...expensable.spendingAlertsSource]}
              locale={locale}
            />
          )
        },
      },
      {
        identifier: 'effectiveOn',
        title: i18n.t('expenses_v2.list.header.date'),
        kind: ProjectionKind.Custom,
        template: '{{ effectiveOn | date: "%Y-%m-%d" }}',
        render: ({ effectiveOn }) => {
          return <EffectiveOn date={effectiveOn} />
        },
        sortBy: 'effectiveOn',
      },
      {
        identifier: 'createdAt',
        title: i18n.t('expenses_v2.list.header.created_at'),
        kind: ProjectionKind.Custom,
        hidden: true,
        template: '{{ createdAt | date: "%Y-%m-%d" }}',
        render: ({ createdAt }) => {
          return <EffectiveOn date={createdAt} />
        },
        sortBy: 'createdAt',
      },

      {
        identifier: 'documentType',
        title: i18n.t('expenses_v2.list.header.document_type'),
        hidden: true,
        kind: ProjectionKind.Custom,
        template: '{% if expense %} {{expense.documentType}} {% endif %}',
        render: ({ expense }) => {
          return expense?.documentType ? (
            <DocumentType
              documentType={expense?.documentType}
              customizationFields={customizationFields}
            />
          ) : null
        },
      },
      {
        identifier: 'paymentMethod',
        title: i18n.t('expenses_v2.list.header.payment_method'),
        kind: ProjectionKind.Custom,
        hidden: true,
        template:
          '{% if expense && expense.creationType === "manual" %} {{expense.paymentMethod}} {% else %} {{expense.card.last4}} {% endif %}',
        render: ({ expense }) => {
          if (!expense) return null
          const { creationType, paymentMethod, card } = expense
          if (creationType === 'manual') {
            return paymentMethod ? (
              <PaymentMethod
                paymentMethod={paymentMethod}
                customizationFields={customizationFields}
              />
            ) : null
          }
          return card ? <Card card={card} /> : null
        },
      },

      {
        identifier: 'amount',
        title: i18n.t('expenses_v2.list.header.amount'),
        kind: ProjectionKind.Custom,
        template: '{{ amount | divided_by: 100}} {{currency}}',
        render: ({ amount, currency }) => {
          return <Amount amount={Number(amount)} currency={currency} />
        },
        width: 150,
        sortBy: 'amount',
      },
      {
        identifier: 'reimbursableAmount',
        hidden: true,
        title: i18n.t('expenses_v2.list.header.reimbursable_amount'),
        kind: ProjectionKind.Custom,
        template: '{{ reimbursableAmount | divided_by: 100}} {{reimbursableCurrency}}',
        render: ({ reimbursableAmount, reimbursableCurrency }) => {
          return (
            reimbursableCurrency && (
              <Amount amount={Number(reimbursableAmount)} currency={reimbursableCurrency} />
            )
          )
        },
        width: 220,
        sortBy: 'reimbursableAmount',
      },
      {
        identifier: 'category',
        title: i18n.t('expenses_v2.list.header.category'),
        kind: ProjectionKind.Custom,
        template:
          '{% if expense %} {{expense.category.name}} {% else %} {{mileage.category.name}} {% endif %}',
        render: ({ expense, mileage }) => {
          return <Category expensable={expense || mileage} />
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: (expensable: Expensable) => {
          return <Actions expensable={expensable} {...actions} />
        },
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        width: 70,
        render: ({ id, currency, amount, effectiveOn, owner }) => {
          if (!id) return null
          return (
            <Detail
              id={id}
              effectiveOn={effectiveOn}
              currency={currency}
              amount={Number(amount)}
              owner={owner}
            />
          )
        },
      },
      {
        identifier: 'reference',
        title: i18n.t('expenses_v2.list.header.internal_reference'),
        kind: ProjectionKind.Path,
        path: 'internalReference',
        hidden: true,
      },
    ],
    {
      variables: {
        groupIds: group ? [group.id] : null,
        search: debouncedSearch,
        includeManualDrafts: isMyExpensesSection(section),
      },
    }
  )

  const clearQuickFilters = () => {
    if (status || pendingApproval) clearFilters()
  }

  const datatableFilters = allFilters({
    expensesContext: section ?? 'company',
    withinGroup: !!group,
    categories,
    currencies,
    subcategories,
    initialFilterValues: {
      status,
      pendingApproval,
    },
    clearQuickFilters,
    includeReportedByFilter: hasReportOnBehalfFeature,
    canCreateExpensesForOthers,
    employeeId: employee?.id,
  })

  return (
    <DataTable.Provider
      persistFilters={!status && !pendingApproval}
      enableClearFilters
      filters={datatableFilters}
      pagination={{
        kind: 'cursor',
        itemsPerPage: 25,
        infiniteScroll: true,
      }}
      source={source}
      sort={{ path: 'effectiveOn', direction: 'asc' }}
    >
      <Modals />
      <Outlet context={{ ...context, back, canSeeTransactions, group }} />
      <Box gap="s16">
        <Header
          search={search}
          onSearch={onSearch}
          {...actions}
          group={group}
          canCreateExpenses={canCreateExpenses}
        />
        <MyExpensesInsights employee={employee} groupId={group?.id} />
        <DataTable.Table
          emptyState={
            isMyExpensesSection(section) && group && isDraftGroup(group) ? (
              <EmptyState canCreateExpenses={canCreateExpenses} canUsePerDiems={canUsePerDiems} />
            ) : null
          }
          selectable={(expensable) =>
            canAnyExpensableAction(
              expensableGroupsEnabled,
              expensable,
              canSyncWithPayroll,
              canSyncWithBanking,
              // We don't allow to restart approval flow in my expenses
              false,
              // We don't allow to set to paid in my expenses
              false,
              section,
              employee
            )
          }
        />
      </Box>
    </DataTable.Provider>
  )
}
